import { useEffect } from 'react';
import { TextInput, View } from 'react-native';

import { FiltersToggle } from './FiltersToggle';
import { dismissSoftKeyboard } from '../primitives/dismissSoftKeyboard';
import { useLiveAnnouncement } from '../primitives/useLiveAnnouncement';
import type { PickerStyles } from './pickerStyles';
import type { UiTheme } from '../theme';

// Search box + the filters toggle beside it: how the exercise list is narrowed.
// Extracted from ExercisePicker (PE-40) to keep that file under the 500 cap
// after the Workout tab. A pure move.
export function PickerSearchRow({
  theme,
  styles,
  t,
  query,
  setQuery,
  filtersOpen,
  setFiltersOpen,
  activeFilterCount,
  resultCount,
}: {
  theme: UiTheme;
  styles: PickerStyles;
  t: (key: string, opts?: Record<string, unknown>) => string;
  query: string;
  setQuery: (v: string) => void;
  filtersOpen: boolean;
  setFiltersOpen: (v: boolean) => void;
  activeFilterCount: number;
  /** How many exercises the list shows now; spoken on submit (DS-72); `null` while the catalog loads. */
  resultCount: number | null;
}) {
  // DS-72: on web the return key no longer blurs (dismissSoftKeyboard is a
  // no-op there), so it would give a screen-reader user nothing back. It says
  // how many results the search left instead, and only once they are known.
  const { announce, clear, region } = useLiveAnnouncement();
  // A count that moves without typing (a filter toggled) makes the standing
  // announcement stale. Keyed on `resultCount` only: submit does not change it,
  // so this never fires between a submit and its tick.
  useEffect(() => {
    clear();
  }, [resultCount, clear]);
  const submit = () => {
    dismissSoftKeyboard();
    if (resultCount === null) return;
    announce(t('mobile.logger.pickerResultsN', { count: resultCount }));
  };
  return (
    <View style={styles.searchWrap}>
      <TextInput
        style={styles.search}
        placeholder={t('mobile.logger.pickerSearch')}
        placeholderTextColor={theme.palette.ink3}
        autoCorrect={false}
        autoCapitalize="none"
        value={query}
        onChangeText={(v) => {
          clear();
          setQuery(v);
        }}
        // The list filters as you type, so the return key has nothing left to
        // submit — its one job is to get the keyboard out of the way (LG-33).
        returnKeyType="search"
        onSubmitEditing={submit}
        // A placeholder is not a name (WCAG 4.1.2, 3.3.2): it vanishes once
        // you type and some readers skip it. Same string, so eye and ear agree.
        accessibilityLabel={t('mobile.logger.pickerSearch')}
      />
      {region}
      <FiltersToggle
        theme={theme}
        open={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
        activeCount={activeFilterCount}
        t={t}
      />
    </View>
  );
}
